import React, { useCallback, useContext } from 'react';
import Raven from 'raven-js';
import PropTypes from 'prop-types';
import { useQuery, gql } from '@apollo/client';
import { analyticService } from 'global/services';

import { LocaleContext } from 'app/containers/LocaleProvider';
import Responsive from 'app/components/Responsive';
import { BasketReceiptLanding } from 'app/views/Basket/components/BasketCheck/BasketReceiptLanding';
import { tp } from 'app/utils/typograf';

import classNames from 'classnames';
import { AuthContext } from 'app/containers/AuthContainer';
import { promocodeVar } from 'app/apollo/reaction';
import { BasketContext } from '../../BasketProvider';
import { BillingContext } from '../../BillingProvider';

import s from './order-details-boxes.module.scss';
import { BoxesCheckForm } from './components/BoxesCheckForm';

import InfoIcon from './img/info.component.svg';
import { ConfigContext } from '../../ConfigProvider';


const locales = {
    ru: {
        titleMobile: 'К оплате',
        titleDesktop: 'К оплате',
    },
    en: {
        titleMobile: 'Order',
        titleDesktop: 'Order',
    },
};


export function OrderDetailsBoxes(props) {
    const {
        pageId,
    } = props;

    const { isAuthed } = useContext(AuthContext);
    const { locale } = useContext(LocaleContext);
    const l = locales[locale];

    const { data: { user } } = useQuery(gql`
        query USER_PHONE {
            user {
                phone
            }
        }
    `, {
        context: {
            massage: 'app:fetch:AvailableLinksProvider',
        },
    });

    const {
        basket: {
            discount_conditions: promoocodeConditions,
            totals: {
                dishesPrice,
                deliveryPrice,
                promocode_amount: promocodeAmount,
                total_common_price: totalCommonPrice,
                cashback_amount: cashbackAmount,
                referral_amount: referralAmount,
            },
        },
        refetchBasket,
    } = useContext(BasketContext);

    const {
        updateBilling,
        billingState: { promocode },
    } = useContext(BillingContext);
    const config = useContext(ConfigContext);

    const applyPromocodeHandler = useCallback(async ({ nextPromocode }) => {
        const phone = (user && user.phone) || '';

        try {
            await updateBilling({
                phone,
                promocode: nextPromocode,
            });
            const [basketResp] = await refetchBasket();

            const {
                data: { cart: { discount_conditions: dc } },
            } = basketResp;

            promocodeVar(dc.promocode);

            analyticService.push({
                eventName: 'Check_Send_Promo',
                sourceName: pageId,
                isPromoCodeExists: dc.isPromoCodeExists,
                promocodeStatus: dc.promocode_status,
                isPromocodeCombineWithDiscount: dc.isPromocodeCombineWithDiscount,
                isSubscriptionActivated: dc.isSubscriptionActivated,
                promo: dc.promocode,
            });

            return { promocodeConditions: dc };
        } catch (error) {
            Raven.captureException(error);
        }
    }, [updateBilling, refetchBasket, user, pageId]);

    const desktopCheckClasses = classNames({
        [s['check-area-container']]: true,
        [s['desktop-only']]: true,
    });

    const mobileCheckClasses = classNames({
        [s['check-area-container']]: true,
        [s['mobile-only']]: true,
    });

    const isPromocodeShown = !config?.hidePromocodeRow && !cashbackAmount && !referralAmount;

    return (
        <div className={s['order-details-boxes']}>
            <div className={s['order-details__container']}>
                <div className={s['order-details__title-container']}>
                    <p className={s['order-details__title']}>
                        <Responsive
                            mobile={l.titleMobile}
                            desktop={l.titleDesktop}
                        />
                    </p>
                </div>
                <div className={s['order-details__receipt']}>
                    <BasketReceiptLanding
                        dishesPrice={dishesPrice}
                        deliveryPrice={deliveryPrice}
                        promocodeAmount={promocodeAmount}
                        totalCommonPrice={totalCommonPrice}
                        promocodeConditions={promoocodeConditions}
                        applyPromocodeHandler={applyPromocodeHandler}
                        promocode={promocode}
                        isWelcomeDiscountShown={false}
                        pageId={pageId}
                        hasSubmitBtn
                        isPromocodeShown={isPromocodeShown}
                    />
                </div>
                <div className={desktopCheckClasses}>
                    <BoxesCheckForm {...props} />
                    {!isAuthed && (
                        <div className={s['enter-phone-policy']}>
                            {tp.execute('Нажимая «Оформить заказ», вы соглашаетесь с условиями')}
                            <a
                                href="/offer/"
                                rel="noopener noreferrer"
                            >
                                {' '}
                                <span className={s['enter-phone-policy__link']}>
                                    пользовательского соглашения,
                                </span>
                                {' '}
                            </a>
                            политиками
                            <a
                                href="/privacy_policy/"
                                rel="noopener noreferrer"
                            >
                                {' '}
                                <span className={s['enter-phone-policy__link']}>
                                    конфиденциальности,
                                </span>
                            </a>
                            <a
                                href="/personal_data_policy/"
                                rel="noopener noreferrer"
                            >
                                {' '}
                                <span className={s['enter-phone-policy__link']}>
                                    обработки перс. данных
                                </span>
                                {' '}
                            </a>
                            <a
                                href="/mailing_policy/"
                                rel="noopener noreferrer"
                            >
                                {' '}
                                и
                                {' '}
                                <span className={s['enter-phone-policy__link']}>
                                    рассылки
                                </span>
                            </a>
                        </div>
                    )}
                    <div className={s.orderInfoBlock}>
                        <span className={s.orderInfoTitle}>Телефонный номер нужен для активации промокода на первый заказ.</span>
                        <span className={s.orderInfoSubtitle}> Заказ можно оформить и подтвердить онлайн без звонков.</span>
                    </div>
                </div>
            </div>
            <div className={mobileCheckClasses}>
                <BoxesCheckForm {...props} />
                <div className={s['enter-phone-policy']}>
                    {!isAuthed && (
                        <>
                            {tp.execute('Нажимая «Оформить заказ», вы соглашаетесь с условиями')}
                            <a
                                href="/offer/"
                                rel="noopener noreferrer"
                            >
                                {' '}
                                <span className={s['enter-phone-policy__link']}>
                                    пользовательского соглашения,
                                </span>
                                {' '}
                            </a>
                            <br className={s['enter-phone-policy-row-break']} />
                            политиками
                            <a
                                href="/privacy_policy/"
                                rel="noopener noreferrer"
                            >
                                {' '}
                                <span className={s['enter-phone-policy__link']}>
                                    конфиденциальности,
                                </span>
                            </a>
                            <a
                                href="/personal_data_policy/"
                                rel="noopener noreferrer"
                            >
                                {' '}
                                <span className={s['enter-phone-policy__link']}>
                                    обработки перс. данных
                                </span>
                            </a>
                            <a
                                href="/mailing_policy/"
                                rel="noopener noreferrer"
                            >
                                {' '}
                                и
                                {' '}
                                <span className={s['enter-phone-policy__link']}>
                                    рассылки
                                </span>
                            </a>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

OrderDetailsBoxes.propTypes = {
    pageId: PropTypes.string.isRequired,
};
